import { Form } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import {
    createColumnInlineCheckbox,
    createColumnInlineTextArea,
    createColumnInput,
    createColumnSelect
} from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import { createButtonCreate } from "../../../../utils/button";
import { conditionFormChecker, onClickDeleteAllClient } from "../../../../utils/form";
import { onIFrameClickSelectAll } from "../../../../utils/iframe";
import { createLoading } from "../../../../utils/loading";
import { has } from "../../../../utils/menu-context";
import { createManageDelete, createManageExportCsv, createPage } from "../../../../utils/page";
import {
    callAquaticType,
    callProductConsumption,
    callProductType,
    openModalList as openModalListProduct
} from "../../master-mgt/product/product.logic";
import { callCompanyDocRejectType } from "../company-doc-reject/company-doc-reject.section";
import { callSamplingCondition } from "../company/company.section";

const CSelect = defineAsyncComponent(() => import("../../../../components/input/template-builder/select/CSelect"));

export const { isLoading, setIsLoading } = createLoading();

export const callCompanyProductStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_PRODUCT_STATUS", value: searchQuery });
export const callCompanyProductStatusByKey = ({ key }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_PRODUCT_STATUS", key });
export const callSamplingConditionByKey = ({ key }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAMPLING_CONDITION", key });
export const callDocRejectStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "DOC_REJECT_STATUS", value: searchQuery });
export const callSamplingFrequencyType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAMPLING_FREQUENCY_TYPE", value: searchQuery });

export const createColumns = ({ props, data }) => {
    const {
        itemsKey,
        visibleIsSuspendedSampling,
        visibleIsSuspendedHc,
        visibleCheckPrivilege,
        visibleCheckReject,
        visibleSamplingCondition,
        visibleSamplingFrequencyType,
        visibleDocRejectStatus,
        visibleCompany,
        visibleProduct,
        disabledCheckPrivilege,
        disabledCheckReject,
        disabledIsSuspendedSampling,
        disabledIsSuspendedHc,
        disabledSamplingCondition,
        disabledSamplingFrequencyType,
        disabledStatus,
        disabledDocRejectStatus,
        disabledRemark,
        remarkTitle
    } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    const columns = [];
    if (visibleCheckPrivilege) {
        columns.push(
            createColumnInlineCheckbox({
                title: () => i18n.global.t("model.companyProduct.isCheckedPrivilege"),
                dataIndex: "isCheckedPrivilege",
                customRenderInputProp: ({ record }) => ({ disabled: disabledCheckPrivilege || !["Approve"].includes(record.refStatus?.key) })
            })
        );
    }
    if (visibleCheckReject) {
        columns.push(
            createColumnInlineCheckbox({
                title: () => i18n.global.t("model.companyProduct.isCheckedReject"),
                dataIndex: "isCheckedReject",
                customRenderInputProp: ({ record }) => ({ disabled: disabledCheckReject || ["Cancel"].includes(record.refStatus?.key) })
            })
        );
    }
    if (visibleCompany) {
        columns.push(createColumnInput({ title: () => i18n.global.t("model.company.thNo"), dataIndex: "refCompany.thNo", align: "center" }));
        columns.push(createColumnInput({ title: () => i18n.global.t("model.company.nameTh"), dataIndex: "refCompany.nameTh", width: 200 }));
        columns.push(createColumnInput({ title: () => i18n.global.t("model.company.nameEn"), dataIndex: "refCompany.nameEn", width: 200 }));
    }
    if (visibleProduct) {
        columns.push(
            createColumnInput({
                title: () => i18n.global.t("model.product.code"),
                dataIndex: "refProduct.code",
                align: "center",
                customRender: ({ record }) => <div class={record.refProduct?.isActive ? "" : "product-inactive"}>{record.refProduct?.code}</div>
            })
        );
        columns.push(createColumnInput({ title: () => i18n.global.t("model.product.nameTh"), dataIndex: "refProduct.nameTh", width: 200 }));
        columns.push(createColumnInput({ title: () => i18n.global.t("model.product.nameEn"), dataIndex: "refProduct.nameEn", width: 200 }));
        columns.push(
            createColumnSelect({
                visible: () => false,
                title: () => i18n.global.t("model.product.refProductType"),
                dataIndex: "refProduct.refProductType.value",
                inputProp: () => ({
                    list: callProductType,
                    renderLabel: ({ item }) => (
                        <div>
                            {item.value} ({item.valueEn})
                        </div>
                    )
                }),
                search: {
                    name: "refProduct.refProductType.id"
                }
            })
        );
        columns.push(
            createColumnSelect({
                visible: () => false,
                title: () => i18n.global.t("model.product.refProductConsumption"),
                dataIndex: "refProduct.refProductConsumption.value",
                inputProp: () => ({
                    list: callProductConsumption,
                    renderLabel: ({ item }) => (
                        <div>
                            {item.value} ({item.valueEn})
                        </div>
                    )
                }),
                search: {
                    name: "refProduct.refProductConsumption.id"
                }
            })
        );
        columns.push(
            createColumnSelect({
                visible: () => false,
                title: () => i18n.global.t("model.product.refAquaticType"),
                dataIndex: "refProduct.refAquaticType.value",
                inputProp: () => {
                    return {
                        list: callAquaticType,
                        renderLabel: ({ item }) => (
                            <div>
                                {item.value} ({item.valueEn})
                            </div>
                        )
                    };
                },
                search: {
                    name: "refProduct.refAquaticType.id"
                }
            })
        );
    }
    if (visibleIsSuspendedSampling) {
        columns.push(
            createColumnInlineCheckbox({
                title: () => i18n.global.t("model.companyProduct.isSuspendedSampling"),
                dataIndex: "isSuspendedSampling",
                customRenderInputProp: ({ record }) => ({
                    disabled: disabledIsSuspendedSampling || ["Suspend", "Cancel"].includes(record.refStatus?.key)
                })
            })
        );
    }
    if (visibleIsSuspendedHc) {
        columns.push(
            createColumnInlineCheckbox({
                title: () => i18n.global.t("model.companyProduct.isSuspendedHc"),
                dataIndex: "isSuspendedHc",
                customRenderInputProp: ({ record }) => ({ disabled: disabledIsSuspendedHc || ["Suspend", "Cancel"].includes(record.refStatus?.key) })
            })
        );
    }
    if (visibleSamplingCondition) {
        columns.push(
            createColumnSelect({
                title: () => i18n.global.t("model.companyProduct.refSamplingCondition"),
                dataIndex: "refSamplingCondition.value",
                inputProp: () => ({ list: callSamplingCondition, optLabel: "value" }),
                customRender: ({ record }) => {
                    const index = items.findIndex(d => d === record);
                    return (
                        <Form.Item {...useForm.validateInfos[`${itemsKey}.${index}.refSamplingCondition`]} wrapperCol={{ xs: 24 }}>
                            <CSelect
                                value={record.refSamplingCondition}
                                onUpdate:value={value => {
                                    record.isSave = true;
                                    record.refSamplingCondition = value;
                                    callSamplingFrequencyType({}).then(samplingFrequencyTypes => {
                                        let refSamplingFrequencyType = samplingFrequencyTypes.find(d => d.key === "L");
                                        if (value?.key !== "L") {
                                            refSamplingFrequencyType = samplingFrequencyTypes[samplingFrequencyTypes.length - 1];
                                        }
                                        record.refSamplingFrequencyType = refSamplingFrequencyType;
                                    });
                                }}
                                list={callSamplingCondition}
                                optLabel="value"
                                disabled={disabledSamplingCondition}
                                allowClear={false}
                            ></CSelect>
                        </Form.Item>
                    );
                },
                search: { name: "refSamplingCondition.id" },
                width: 200
            })
        );
    }
    if (visibleSamplingFrequencyType) {
        columns.push(
            createColumnSelect({
                title: () => i18n.global.t("model.companyProduct.refSamplingFrequencyType"),
                dataIndex: "refSamplingFrequencyType.value",
                customRender: ({ record }) => {
                    const index = items.findIndex(d => d === record);
                    const disabled = record.refSamplingCondition?.key === "L" || disabledSamplingFrequencyType;
                    return (
                        <Form.Item {...useForm.validateInfos[`${itemsKey}.${index}.refSamplingFrequencyType`]} wrapperCol={{ xs: 24 }}>
                            <CSelect
                                value={record.refSamplingFrequencyType}
                                onUpdate:value={value => {
                                    record.isSave = true;
                                    record.refSamplingFrequencyType = value;
                                }}
                                list={({ searchQuery }) =>
                                    callSamplingFrequencyType({ searchQuery }).then(samplingFrequencyTypes =>
                                        samplingFrequencyTypes.filter(d => !["L"].includes(d.key))
                                    )
                                }
                                optLabel="value"
                                disabled={disabled}
                                allowClear={false}
                            ></CSelect>
                        </Form.Item>
                    );
                },
                search: { name: "refSamplingFrequencyType.id" },
                inputProp: () => ({ list: callSamplingFrequencyType, optLabel: "value" })
            })
        );
    }
    columns.push(
        createColumnSelect({
            title: () => i18n.global.t("model.companyProduct.refStatus"),
            dataIndex: "refStatus.value",
            inputProp: () => ({ list: callCompanyProductStatus, optLabel: "value" }),
            customRender: ({ record }) => {
                const index = items.findIndex(d => d === record);
                const key = `${itemsKey}.${index}.refStatus`;
                const inputProp = lodash.merge(
                    {
                        value: record.refStatus,
                        "onUpdate:value": value => {
                            record.isSave = true;
                            record.refStatus = value;
                            if (["Suspend", "Cancel"].includes(value?.key)) {
                                record.isSuspendedSampling = true;
                                record.isSuspendedHc = true;
                            }
                        },
                        list: callCompanyProductStatus,
                        optLabel: "value",
                        disabled: disabledStatus,
                        allowClear: false
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                );
                return (
                    <Form.Item {...useForm.validateInfos[key]} wrapperCol={{ xs: 24 }}>
                        <CSelect {...inputProp}></CSelect>
                    </Form.Item>
                );
            },
            search: { name: "refStatus.id" }
        })
    );
    if (visibleDocRejectStatus) {
        columns.push(
            createColumnSelect({
                title: () => i18n.global.t("model.companyProduct.refDocRejectStatus"),
                dataIndex: "refDocRejectStatus.value",
                inputProp: () => ({ list: callCompanyDocRejectType, optLabel: "value" }),
                customRender: ({ record }) => {
                    const index = items.findIndex(d => d === record);
                    const key = `${itemsKey}.${index}.refDocRejectStatus`;
                    const inputProp = lodash.merge(
                        {
                            value: record.refDocRejectStatus,
                            "onUpdate:value": value => {
                                record.isSave = true;
                                record.refDocRejectStatus = value;
                            },
                            list: callCompanyDocRejectType,
                            optLabel: "value",
                            disabled: disabledDocRejectStatus
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                    );
                    return (
                        <Form.Item {...useForm.validateInfos[key]} wrapperCol={{ xs: 24 }}>
                            <CSelect {...inputProp}></CSelect>
                        </Form.Item>
                    );
                },
                search: { name: "refDocRejectStatus.id" }
            })
        );
    }
    columns.push(
        createColumnInlineTextArea({
            title: () => remarkTitle,
            dataIndex: "remark",
            items,
            useForm,
            itemsKey,
            customRenderInputProp: () => ({ disabled: disabledRemark })
        })
    );
    return columns;
};

export const initData = ({ props, data }) => {
    const { menuContext, viewOnly, itemsKey, visibleCompany, visibleProduct, selectGetCheckboxPropDisabled } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    data.columns = createColumns({ props, data });
    data.page = createPage({ data: items });
    data.tableOptions = { search: { activeKey: null } };
    data.manages = [
        createManageDelete({
            menuContext,
            visible: () => (viewOnly ? false : has({ menuContext, key: "canDelete" }) || props.canDelete),
            onClick: ({ rowSelection }) => onClickDeleteAllClient({ rowSelection, useForm, items, key: itemsKey })
        }),
        createManageExportCsv({ menuContext })
    ];
    data.manageExtras = [];
    data.rowActions = [];

    if (!viewOnly) {
        if (visibleCompany) {
            /*
            data.manageExtras.push({
                render: () => ({
                    ...createButtonCreate({
                        onClick: () => openModalListCompany({
                            onIframeMounted: ({ refs, options }) => onIFrameClickSelectAll({
                                refs, options, items, itemKey: 'refCompany.id', rowKey: 'id', setKey: 'refCompany'
                            }),
                        }),
                        options: { text: i18n.global.t('model.companyProduct.refCompany') }
                    }),
                }),
                visible: () => has({ menuContext, key: 'canCreate' }),
            })
            */
        }
        if (visibleProduct) {
            data.manageExtras.push({
                render: () => ({
                    ...createButtonCreate({
                        onClick: () =>
                            openModalListProduct({
                                onIframeMounted: ({ refs, options }) => {
                                    onIFrameClickSelectAll({
                                        refs,
                                        options,
                                        items,
                                        itemKey: "refProduct.id",
                                        rowKey: "id",
                                        setKey: "refProduct",
                                        afterSelect: () => {
                                            Promise.all([
                                                callCompanyProductStatusByKey({ key: "Draft" }),
                                                callSamplingConditionByKey({ key: "L" })
                                            ]).then(arr => {
                                                const approveStatusDraft = arr[0][0];
                                                const samplingConditionDraft = arr[1][0];
                                                items.forEach(item => {
                                                    if (!item.refStatus) item.refStatus = approveStatusDraft;
                                                    if (!item.refSamplingCondition) item.refSamplingCondition = samplingConditionDraft;
                                                });
                                            });
                                        }
                                    });
                                    refs.iframe.contentWindow.selectGetCheckboxPropDisabled = selectGetCheckboxPropDisabled;
                                }
                            }),
                        options: { text: i18n.global.t("model.companyProduct.refProduct") }
                    })
                }),
                visible: () => has({ menuContext, key: "canCreate" })
            });
        }
    }
};
